import { Box, Typography, Table, TableBody, TableCell, TableRow } from '@mui/material';
import type { Patient } from '@/types/patient';

interface PrintableTestResultsProps {
  testId?: string;
  testInfo?: {
    createdAt: string;
    operatorName?: string;
    deviceUid?: string;
    status?: string;
    sampleCode?: string;
  };
  measurementResults: any;
  patient?: Patient | null;
  notes?: string;
  additionalInfo?: any;
}

/**
 * PrintableTestResults - Optimized single-page A4 print layout
 * Hidden on screen, only visible when printing
 */
export function PrintableTestResults({
  testId,
  testInfo,
  measurementResults,
  patient,
  notes,
  additionalInfo,
}: PrintableTestResultsProps) {
  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const formatPatientDOB = (dob: Date) => {
    try {
      const date = new Date(dob);
      const age = Math.floor((Date.now() - date.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
      return `${date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })} (${age}y)`;
    } catch {
      return 'N/A';
    }
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const getResultColor = (status: string): string => {
    const lower = status.toLowerCase();
    if (lower === 'negative') return '#4caf50'; // green
    if (lower === 'positive') return '#f44336'; // red
    if (lower === 'analyzing' || lower === 'retrying') return '#ff9800'; // orange
    if (lower === 'error') return '#f44336'; // red
    return '#9e9e9e'; // gray
  };

  const channelResults = measurementResults?.channels || [];

  return (
    <Box className="printable-results" sx={{
      fontFamily: 'Arial, sans-serif',
      fontSize: '10pt',
      lineHeight: 1.4,
      color: '#000',
      backgroundColor: '#fff',
      p: 0,
    }}>
      {/* Header with Logo and Title */}
      <Box className="print-header" sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        borderBottom: '2px solid #1976d2',
        pb: 0.5,
        mb: 1,
      }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, fontSize: '13pt', color: '#1976d2', mb: 0.3 }}>
            Ediphor LabCloud
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 600, fontSize: '10pt', color: '#333' }}>
            Respiratory Panel Test Results
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right', fontSize: '8pt' }}>
          <Typography variant="body2" sx={{ fontSize: '9pt', fontWeight: 600, color: '#1976d2', mb: 0.3 }}>
            {testInfo?.createdAt ? formatDate(testInfo.createdAt) : 'N/A'}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '8pt', color: '#666' }}>
            <strong>Test ID:</strong> {testId || 'N/A'}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '8pt', color: '#666' }}>
            <strong>Sample:</strong> {testInfo?.sampleCode || 'N/A'}
          </Typography>
        </Box>
      </Box>

      {/* Test Metadata - 4 columns */}
      <Box className="print-metadata" sx={{ mb: 0.8 }}>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: '7.5pt', border: 'none', py: 0.2, px: 0 }}>
                Device:
              </TableCell>
              <TableCell sx={{ fontSize: '7.5pt', border: 'none', py: 0.2 }}>
                {testInfo?.deviceUid || 'N/A'}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '7.5pt', border: 'none', py: 0.2 }}>
                Operator:
              </TableCell>
              <TableCell sx={{ fontSize: '7.5pt', border: 'none', py: 0.2 }}>
                {testInfo?.operatorName || 'N/A'}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: '7.5pt', border: 'none', py: 0.2, px: 0 }}>
                Status:
              </TableCell>
              <TableCell sx={{ fontSize: '7.5pt', border: 'none', py: 0.2 }} colSpan={3}>
                <Box component="span" sx={{
                  display: 'inline-block',
                  px: 0.6,
                  py: 0.15,
                  borderRadius: '2px',
                  backgroundColor: '#4caf50',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '7pt',
                }}>
                  {testInfo?.status?.toUpperCase() || 'N/A'}
                </Box>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>

      {/* Test Results - 2x2 Grid (Compact) */}
      <Box className="print-results" sx={{ mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '9pt', mb: 0.5, color: '#333' }}>
          Test Results
        </Typography>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 0.8,
        }}>
          {channelResults.map((channel: any) => {
            const status = channel.result || 'Unknown';
            const resultColor = getResultColor(status);
            
            return (
              <Box key={channel.channel} sx={{
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                p: 0.7,
                backgroundColor: '#fafafa',
              }}>
                <Typography sx={{ fontWeight: 600, fontSize: '8.5pt', color: '#333', mb: 0.2 }}>
                  {channel.pathogen}
                </Typography>
                <Box sx={{
                  display: 'inline-block',
                  px: 0.8,
                  py: 0.2,
                  borderRadius: '3px',
                  backgroundColor: resultColor,
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '9pt',
                  textTransform: 'uppercase',
                }}>
                  {status}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Patient Information */}
      {patient && (
        <Box className="print-patient-info" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '9pt', mb: 0.5, color: '#333' }}>
            Patient Information
          </Typography>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: '7.5pt', border: 'none', py: 0.2, px: 0, width: '100px' }}>
                  Patient Name:
                </TableCell>
                <TableCell sx={{ fontSize: '7.5pt', border: 'none', py: 0.2 }}>
                  {patient.fullName}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '7.5pt', border: 'none', py: 0.2, width: '100px' }}>
                  Date of Birth:
                </TableCell>
                <TableCell sx={{ fontSize: '7.5pt', border: 'none', py: 0.2 }}>
                  {formatPatientDOB(patient.dateOfBirth)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: '7.5pt', border: 'none', py: 0.2, px: 0 }}>
                  Patient ID:
                </TableCell>
                <TableCell sx={{ fontSize: '7.5pt', border: 'none', py: 0.2 }}>
                  {patient.id}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '7.5pt', border: 'none', py: 0.2 }}>
                  Sex:
                </TableCell>
                <TableCell sx={{ fontSize: '7.5pt', border: 'none', py: 0.2 }}>
                  {patient.sex}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Notes Section */}
      {notes && (
        <Box className="print-notes" sx={{ mb: 0.8 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '9pt', mb: 0.4, color: '#333' }}>
            Notes
          </Typography>
          <Box sx={{
            backgroundColor: '#f5f5f5',
            border: '1px solid #e0e0e0',
            borderRadius: '3px',
            p: 0.6,
            fontSize: '7.5pt',
            maxHeight: '50px',
            overflow: 'hidden',
          }}>
            {truncateText(notes, 200)}
          </Box>
        </Box>
      )}

      {/* Survey Responses Summary (if available) */}
      {additionalInfo && Object.keys(additionalInfo).length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '10pt', mb: 0.5, color: '#333' }}>
            Survey Summary
          </Typography>
          <Typography sx={{ fontSize: '8pt', color: '#666' }}>
            Additional data collected: {Object.keys(additionalInfo).length} responses
          </Typography>
        </Box>
      )}

      {/* Footer */}
      <Box sx={{
        borderTop: '1px solid #e0e0e0',
        pt: 0.5,
        mt: 0.8,
        fontSize: '6.5pt',
        color: '#666',
        textAlign: 'center',
      }}>
        <Typography sx={{ fontSize: '6.5pt' }}>
          Ediphor LabCloud | Generated on {new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </Typography>
        <Typography sx={{ fontSize: '6.5pt', mt: 0.2 }}>
          This document is for medical professional use only. Results should be interpreted in conjunction with clinical findings.
        </Typography>
      </Box>
    </Box>
  );
}
